import axios from 'axios';

const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
});

// ─────────────────────────────────────────────
// Request Interceptor — Attach JWT Token
// ─────────────────────────────────────────────
API.interceptors.request.use(
  (config) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || 'null');

      if (user?.token) {
        config.headers.Authorization = `Bearer ${user.token}`;
      }
    } catch (error) {
      console.error('Invalid localStorage user data');
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ─────────────────────────────────────────────
// Response Interceptor — Handle Unauthorized
// ─────────────────────────────────────────────
API.interceptors.response.use(
  (response) => response,

  (error) => {
    if (error.response?.status === 401) {

      const publicRoutes = ['/login', '/register'];

      const isPublicRoute = publicRoutes.some(route =>
        window.location.pathname.includes(route)
      );

      if (!isPublicRoute) {
        localStorage.removeItem('user');

        window.location.href = '/login?session=expired';
      }
    }

    return Promise.reject(error);
  }
);

// ─────────────────────────────────────────────
// AUTH API
// ─────────────────────────────────────────────
export const authAPI = {
  register:      (data) => API.post('/auth/register', data),
  login:         (data) => API.post('/auth/login', data),
  getProfile:    ()     => API.get('/auth/profile'),
  updateProfile: (data) => API.put('/auth/profile', data),
  sendOtp:       (email) => API.post('/auth/send-otp', { email }),
  verifyOtp:     (email, otp) => API.post('/auth/verify-otp', { otp }),
};

// ─────────────────────────────────────────────
// PRODUCTS API
// ─────────────────────────────────────────────
export const productsAPI = {
  getAll:      (params) => API.get('/products', { params }),

  getFeatured: () =>
    API.get('/products/featured'),

  getById:     (id) =>
    API.get(`/products/${id}`),

  addReview:   (id, data) =>
    API.post(`/products/${id}/reviews`, data),

  create:      (data) =>
    API.post('/products', data),

  update:      (id, data) =>
    API.put(`/products/${id}`, data),

  delete:      (id) =>
    API.delete(`/products/${id}`),
};

// ─────────────────────────────────────────────
// ORDERS API
// ─────────────────────────────────────────────
export const ordersAPI = {
  create: (data) =>
    API.post('/orders', data),

  getMyOrders: () =>
    API.get('/orders/myorders'),

  getById: (id) =>
    API.get(`/orders/${id}`),

  markPaid: (id, data) =>
    API.put(`/orders/${id}/pay`, data),

  cancel: (id) =>
    API.put(`/orders/${id}/cancel`),


  getAll: (params) =>
    API.get('/orders', { params }),

  updateStatus: (id, status) =>
    API.put(`/orders/${id}/status`, { status }),
};

// ─────────────────────────────────────────────
// PAYMENT API
// ─────────────────────────────────────────────
export const paymentAPI = {

  // Razorpay
  createRazorpayOrder: (amount, notes) =>
    API.post('/payment/razorpay/order', {
      amount,
      notes,
    }),

  verifyRazorpay: (data) =>
    API.post('/payment/razorpay/verify', data),

  // UPI
  createUpiIntent: (amount, orderId) =>
    API.post('/payment/upi/intent', {
      amount,
      orderId,
    }),

  confirmUpi: (utrNumber, amount) =>
    API.post('/payment/upi/confirm', {
      utrNumber,
      amount,
    }),

  checkUpiStatus: (txnRef, orderId) =>
    API.get('/payment/upi/status', {
      params: {
        txnRef,
        orderId,
      },
    }),
};

// ─────────────────────────────────────────────
// UPLOAD API
// ─────────────────────────────────────────────
export const uploadAPI = {
  productImages: (files) => {

    const formData = new FormData();

    files.forEach((file) => {
      formData.append('images', file);
    });

    return API.post(
      '/upload/product-images',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
  },
};

// ─────────────────────────────────────────────
// SETTINGS API
// ─────────────────────────────────────────────
export const settingsAPI = {
  get:    ()       => API.get('/settings'),
  update: (data)   => API.put('/settings', data),
};

// ─────────────────────────────────────────────
// ADMIN API
// ─────────────────────────────────────────────
export const adminAPI = {
  getStats: () =>
    API.get('/admin/stats'),

  getUsers: () =>
    API.get('/admin/users'),

  updateUser: (id, data) =>
    API.put(`/admin/users/${id}`, data),

  deleteUser: (id) =>
    API.delete(`/admin/users/${id}`),

  cleanupGhostOrders: (minutes = 30) =>
    API.post('/admin/cleanup-ghost-orders', { minutes }),
};

export default API;